import { findParent, isGroupNode, type ConditionNode, type GroupNode } from '@customerio/dryad';
import type { TypeOptionGroup } from '../chrome-types';

/** The all/any select a group's operator is chosen with. */
export const GROUP_OPERATOR_OPTIONS = [
  { value: 'and', label: 'All' },
  { value: 'or', label: 'At least one' },
];

/**
 * The add-menu entries that create a nested group.
 *
 * A group is picked by the logic it joins its conditions with, so there are
 * two entries rather than one: picking the operator up front saves opening a
 * group only to change the select inside it. They sit in their own section so
 * they read as a different kind of thing from the condition types above them.
 */
export const ADD_GROUP_AND = 'group:and';
export const ADD_GROUP_OR = 'group:or';

export const ADD_GROUP_OPTION_GROUP: TypeOptionGroup = {
  label: 'Group',
  options: [
    { value: ADD_GROUP_AND, label: 'All (AND)' },
    { value: ADD_GROUP_OR, label: 'At least one (OR)' },
  ],
};

/**
 * The operator a picked add-menu value asks for, or null when the value names
 * a condition type instead.
 */
export function addGroupOperator(value: string): 'and' | 'or' | null {
  if (value === ADD_GROUP_AND) {
    return 'and';
  }
  if (value === ADD_GROUP_OR) {
    return 'or';
  }
  return null;
}

/**
 * The tree mutations these two operations need, as the group chrome receives
 * them from `useBuilderActions`.
 *
 * Declared as the operations' own contract rather than taking a whole
 * `ConditionBuilder`: the builder instance is not reachable from inside a
 * dryad-react component, and naming the four functions used makes both
 * operations callable from a node test with no builder at all.
 */
export type GroupTreeMutations = {
  addGroup: (parentId: string) => void;
  setOperator: (groupId: string, operator: 'and' | 'or') => void;
  moveNode: (id: string, index: number, targetParentId?: string) => boolean;
  removeNode: (id: string) => void;
};

/**
 * Appends a nested group with the given operator.
 *
 * `addGroup` always appends an `and` group, so an `or` group is that same
 * append followed by flipping the operator on what it appended. `treeAfterAdd`
 * reads the tree back, because the appended group's id is assigned by the
 * append and is not returned from it.
 */
export function addChildGroup(
  mutations: Pick<GroupTreeMutations, 'addGroup' | 'setOperator'>,
  treeAfterAdd: () => GroupNode,
  parentId: string,
  operator: 'and' | 'or',
): void {
  mutations.addGroup(parentId);
  if (operator === 'and') {
    return;
  }
  const appended = lastChildOfGroup(treeAfterAdd(), parentId);
  if (appended != null && isGroupNode(appended)) {
    mutations.setOperator(appended.id, operator);
  }
}

function lastChildOfGroup(tree: GroupNode, groupId: string): ConditionNode | null {
  const node = nodeInTree(tree, groupId);
  if (node == null || !isGroupNode(node)) {
    return null;
  }
  return node.children[node.children.length - 1] ?? null;
}

function nodeInTree(tree: GroupNode, id: string): ConditionNode | null {
  if (tree.id === id) {
    return tree;
  }
  const parent = findParent(tree, id);
  return parent?.children.find((child) => child.id === id) ?? null;
}

/**
 * Removes a group but keeps the conditions inside it, lifting them into the
 * parent at the position the group held.
 *
 * The destructive alternative would take rows with it that the user asked
 * nothing about, so the control that looks like a delete is this instead: the
 * group goes, its conditions stay where it was.
 */
export function ungroupNode(
  mutations: Pick<GroupTreeMutations, 'moveNode' | 'removeNode'>,
  tree: GroupNode,
  groupId: string,
): void {
  const located = locateGroupWithParent(tree, groupId);
  if (located == null) {
    return;
  }
  let insertIndex = located.index;
  for (const child of [...located.group.children]) {
    mutations.moveNode(child.id, insertIndex, located.parentId);
    insertIndex = insertIndex + 1;
  }
  mutations.removeNode(groupId);
}

/**
 * The group, its parent and its position among its siblings. Null for the root
 * group, which has no parent to lift anything into.
 */
function locateGroupWithParent(
  tree: GroupNode,
  groupId: string,
): { group: GroupNode; parentId: string; index: number } | null {
  const parent = findParent(tree, groupId);
  if (parent == null) {
    return null;
  }
  const index = parent.children.findIndex((child) => child.id === groupId);
  const group = parent.children[index];
  if (group == null || !isGroupNode(group)) {
    return null;
  }
  return { group, parentId: parent.id, index };
}

/**
 * Whether any nested group in the tree holds no conditions.
 *
 * dryad's `isValid()` accepts an empty group, but a group with nothing in it
 * describes no one, so a save gate checks this alongside it. The root is not
 * counted: an empty root is the separate "no conditions yet" state, which the
 * surface decides on by itself.
 */
export function hasEmptyGroup(tree: GroupNode): boolean {
  return tree.children.some((child) => isGroupNode(child) && (child.children.length === 0 || hasEmptyGroup(child)));
}
